import type { Metadata } from "@/lib/head";
import { Link } from "@/lib/nav";
import { CalendarDays, FileText, House, MessageCircle, NotebookText, Wrench, X } from "lucide-react";
import { useEffect, useState, type CSSProperties } from "react";

import { AppStartFootLink } from "@/components/app-start-link";
import CustomerFooter from "@/components/customer-footer";
import CustomerScreenShell from "@/components/customer-screen-shell";
import Pill, { type PillTone } from "@buildpy/ui/pill";
import SectionCard, { sectionCardClass } from "@buildpy/ui/section-card";
import { Button } from "@buildpy/ui/button";
import { cn } from "@buildpy/ui/cn";
import { EMPTY_MARK, formatDate } from "@buildpy/core/lib/format";
import { customerStatementPath } from "@buildpy/core/modules/change-order/current-statement";
import ChangeOrderStatusBadge from "@/modules/change-order/ui/status-badge";
import Money from "@/components/money";
import UnusableLink from "@/modules/contract/ui/unusable-link";
import { contractSharePath } from "@buildpy/core/modules/contract/view";
import { verifyContractShare } from "@/api/actions/contract-share";
import { MESSAGE_CLOSED_MESSAGE } from "@buildpy/core/modules/message/rules";
import MessageCompose from "@/modules/message/ui/message-compose";
import MessageList from "@/modules/message/ui/message-list";
import MessageSeenMarker from "@/modules/message/ui/message-seen-marker";
import {
  customerAsPath,
  customerHousePath,
  customerLogPath,
  customerPhotoPath,
  type CustomerPhoto,
} from "@buildpy/core/modules/project/customer-visible";
import { customerMessagePhotoPath } from "@buildpy/core/modules/message/photo-path";
import {
  PROJECT_LOG_KIND_LABEL,
  PROJECT_PHASE_STATUS_LABEL,
  type ProjectPhaseStatus,
} from "@buildpy/core/modules/project/types";
import CustomerPhotoThumb from "@/modules/project/ui/customer-photo-thumb";
import { APP_PROMO_KEY, browserSeenStore, markSeen, shouldInvite } from "@/modules/project/ui/app-invite";
import { latestLogOf, phaseProgressOf, type LatestLog } from "@/modules/project/ui/customer-home";
import { LOG_KIND_TONE } from "@/modules/project/ui/log-kind-tone";
import ProjectStatusBadge from "@/modules/project/ui/status-badge";
import { canReview, reviewAuthorLabelOf } from "@buildpy/core/modules/review/rules";
import ReviewForm from "@/modules/review/ui/review-form";
import ShareGate from "@/modules/share/ui/share-gate";
import CustomerHeadOrg from "@/modules/share/ui/customer-head-org";
import type { CustomerProjectView } from "@buildpy/core/views/g3b/customer-project";

export const metadata: Metadata = {
  title: "내 현장",
  robots: { index: false, follow: false },
};

interface Props {
  token: string;
  data: CustomerProjectView;
}

const PHASE_TONE: Record<ProjectPhaseStatus, PillTone> = {
  PENDING: "neutral",
  ACTIVE: "progress",
  DONE: "done",
  HOLD: "hold",
};

const Notice = ({ text }: { text: string }) => (
  <CustomerScreenShell>
    <SectionCard as="div" className="mx-auto w-full max-w-sm p-5 text-center text-sm" padding="none" role="alert">
      {text}
    </SectionCard>
    <AppStartFootLink />
  </CustomerScreenShell>
);

// 사진 바이트는 api 가 입장 쿠키로 내준다 — 화면 주소(/c/…) 앞에 /api 를 붙인다.
const PhotoGrid = ({
  token,
  photos,
  testId,
  cols = 3,
}: {
  token: string;
  photos: readonly CustomerPhoto[];
  testId: string;
  cols?: 3 | 4;
}) => (
  <ul className={cn("grid gap-1", cols === 4 ? "grid-cols-4" : "grid-cols-3")} data-testid={testId}>
    {photos.map((p, i) => (
      <li key={p.id}>
        <CustomerPhotoThumb alt={p.caption || p.phase_name || `현장 사진 ${i + 1}`} src={`/api${customerPhotoPath(token, p.id)}`} />
      </li>
    ))}
  </ul>
);

const period = (from: string | null, to: string | null): string =>
  from || to ? `${formatDate(from)} ~ ${formatDate(to)}` : EMPTY_MARK;

const shortDate = (value: string): string => formatDate(value).slice(5);

type OkData = Extract<CustomerProjectView, { kind: "ok" }>;
type OkView = OkData["view"];

// 공사 상태 한 장 — 누구 댁·주소·상태·지금 공정·진행률(공정표가 있을 때만)·주요 날짜.
const StatusCard = ({ view, place }: { view: OkView; place: OkData["place"] }) => {
  const progress = phaseProgressOf(view.phases);
  return (
    <SectionCard className="flex flex-col gap-y-3 text-sm" data-testid="customer-project-summary">
      <div className="flex items-start justify-between gap-2">
        <div className="min-w-0">
          <h1 className="text-lg font-semibold" data-testid="customer-project-place">
            {place.client_name} 고객님 댁
          </h1>
          {place.site_address && (
            <p className="break-keep text-muted-foreground" data-testid="customer-project-address">
              {place.site_address}
            </p>
          )}
          <p className="text-muted-foreground">{view.title}</p>
        </div>
        <ProjectStatusBadge status={view.status} />
      </div>
      {progress.active && (
        <p className="text-base font-medium" data-testid="customer-project-active-phase">
          지금은 {progress.active.name} 공정이에요
        </p>
      )}
      {progress.total > 0 && (
        <div className="flex flex-col gap-y-1.5">
          <div
            aria-label="공정 진행률"
            aria-valuemax={100}
            aria-valuemin={0}
            aria-valuenow={progress.percent}
            className="h-2 overflow-hidden rounded-full bg-muted"
            role="progressbar">
            <div className="h-full w-(--w) rounded-full bg-primary" style={{ "--w": `${progress.percent}%` } as CSSProperties} />
          </div>
          <p className="tabular-nums text-muted-foreground">
            공정 {progress.total}개 중 {progress.done}개 끝남 · {progress.percent}%
          </p>
        </div>
      )}
      <dl className="grid grid-cols-[auto_1fr] gap-x-3 gap-y-1">
        {view.started_at ? (
          <>
            <dt className="text-muted-foreground">착공일</dt>
            <dd className="tabular-nums">{formatDate(view.started_at)}</dd>
          </>
        ) : (
          <>
            <dt className="text-muted-foreground">착공 예정</dt>
            <dd className="tabular-nums">{formatDate(view.planned_start_date)}</dd>
          </>
        )}
        {view.completion_date ? (
          <>
            <dt className="text-muted-foreground">준공일</dt>
            <dd className="tabular-nums">{formatDate(view.completion_date)}</dd>
          </>
        ) : (
          <>
            <dt className="text-muted-foreground">준공 예정</dt>
            <dd className="tabular-nums">{formatDate(view.planned_end_date)}</dd>
          </>
        )}
        {view.warranty_end_date && (
          <>
            <dt className="text-muted-foreground">유지보수 종료</dt>
            <dd className="tabular-nums">{formatDate(view.warranty_end_date)}</dd>
          </>
        )}
      </dl>
    </SectionCard>
  );
};

// 최근 시공일지 한 건 — 누르면 일지 화면(댓글)으로.
const LatestLogCard = ({ token, latest, comments }: { token: string; latest: LatestLog; comments: number }) => (
  <SectionCard as="article" className="flex flex-col gap-y-2" data-testid="customer-project-latest-log">
    <div className="flex flex-wrap items-center gap-2">
      <h2 className="text-sm font-semibold tabular-nums">최근 시공일지 · {shortDate(latest.date)}</h2>
      <Pill tone={LOG_KIND_TONE[latest.log.kind]}>{PROJECT_LOG_KIND_LABEL[latest.log.kind]}</Pill>
      {latest.log.phase_name && (
        <span className="text-sm text-muted-foreground" data-testid="customer-latest-log-phase">
          {latest.log.phase_name}
        </span>
      )}
    </div>
    <p className="line-clamp-3 whitespace-pre-wrap break-words text-base">{latest.log.body}</p>
    {latest.photos.length > 0 && (
      <PhotoGrid cols={4} photos={latest.photos.slice(0, 4)} testId="customer-latest-photos" token={token} />
    )}
    <Button asChild className="w-full" size="lg" variant="outline">
      <Link data-testid="customer-latest-log-link" href={customerLogPath(token, latest.log.id)}>
        {comments > 0 ? `일지 보기 · 댓글 ${comments}` : "일지 보고 댓글 남기기"}
      </Link>
    </Button>
  </SectionCard>
);

// 바로 가기 — 대화방이 없으면 그 칸은 뺀다(5~6칸 → 세 칸씩 두 줄) · 앱 안 주소는 Link 로 라우터 이동(전체 새로고침 없음), # 은 브라우저 기본.
const QuickMenu = ({ token, contractHref, hasThread }: { token: string; contractHref: string; hasThread: boolean }) => {
  const items = [
    { href: "#days", label: "시공일지", Icon: NotebookText },
    { href: "#phases", label: "공사 일정", Icon: CalendarDays },
    { href: contractHref, label: "견적서·계약서", Icon: FileText },
    ...(hasThread ? [{ href: "#messages", label: "담당자와 대화", Icon: MessageCircle }] : []),
    { href: customerAsPath(token), label: "A/S 접수", Icon: Wrench },
    { href: customerHousePath(token), label: "우리 집 기록", Icon: House },
  ];
  return (
    <SectionCard
      as="ul"
      className="grid grid-cols-3 gap-1 p-2"
      data-testid="customer-project-menu"
      padding="none">
      {items.map(({ href, label, Icon }) => (
        <li key={label}>
          <Link className="flex flex-col items-center gap-y-1.5 rounded-md px-1 py-2 text-center text-sm hover:bg-muted" href={href}>
            <span className="flex size-10 items-center justify-center rounded-full bg-accent text-accent-foreground">
              <Icon aria-hidden className="size-5" />
            </span>
            <span className="break-keep">{label}</span>
          </Link>
        </li>
      ))}
    </SectionCard>
  );
};

// 앱 안내 — 앱 안이거나 한 번 닫았으면 안 보인다(첫 그림은 숨기고 마운트 뒤 판정 — 하이드레이션이 어긋나지 않게).
const AppPromo = () => {
  const [show, setShow] = useState(false);
  useEffect(() => setShow(shouldInvite(APP_PROMO_KEY)), []);
  if (!show) return null;
  const close = () => {
    markSeen(browserSeenStore(), APP_PROMO_KEY);
    setShow(false);
  };
  return (
    <SectionCard as="aside" className="flex items-start gap-x-3" data-testid="customer-app-promo">
      <div className="min-w-0 flex-1">
        <p className="text-base font-semibold">새 시공일지가 오면 바로 알려드릴까요</p>
        <p className="text-sm text-muted-foreground">앱으로 받으면 알림으로 먼저 와요</p>
        {/* 스토어 주소가 아직 없다 — 생기면 여기서 연다. */}
        <Button className="mt-2" size="sm" variant="primary" onClick={close}>
          앱 받기
        </Button>
      </div>
      <Button aria-label="앱 안내 닫기" size="icon-xs" variant="ghost-muted" onClick={close}>
        <X />
      </Button>
    </SectionCard>
  );
};

// 고객 「내 현장」 — 계약 링크 토큰 + 입장 쿠키로 여는 화면(폰 먼저 · 상태 → 확인할 일 → 최근 일지 → 바로 가기 → 상세).
const Page = ({ token, data }: Props) => {
  if (data.kind === "dead") return <UnusableLink token={token} />;
  if (data.kind === "gate") {
    return (
      <CustomerScreenShell>
        <ShareGate docName="현장 소식" orgName={data.orgName} token={token} verify={verifyContractShare} />
        <AppStartFootLink />
      </CustomerScreenShell>
    );
  }
  if (data.kind === "notice") return <Notice text={data.text} />;

  // 읽음은 렌더에서 옮기지 않는다 — 대화 목록이 화면에 들어왔을 때 MessageSeenMarker 가 옮긴다.
  const { orgName, company, place, view, changeOrders, thread, reviewShare, logCommentCounts } = data;
  const latest = latestLogOf(view.days);
  const waiting = changeOrders.filter((co) => co.status === "SENT");
  const unread = thread ? thread.unread : 0;
  const seenUntil = thread ? thread.seenUntil : null;
  const contractHref = contractSharePath(token);

  return (
    <CustomerScreenShell className="p-0 pb-10">
      <header className="sticky top-0 z-10 flex items-center justify-between gap-2 border-b border-border bg-card px-4 pb-2.5 pt-safe-2.5">
        <CustomerHeadOrg name={orgName} tel={company.tel} />
        {/* 현재 내역서는 체결된 계약이면 변경공사가 없어도 연다(원 계약 내역만 보인다). */}
        <div className="flex shrink-0 items-center gap-x-3">
          <Link
            className="tap-target relative text-sm text-muted-foreground underline-offset-2 hover:underline"
            data-testid="customer-project-statement-link"
            href={customerStatementPath(token)}>
            현재 내역서
          </Link>
          <Link className="tap-target relative text-sm text-muted-foreground underline-offset-2 hover:underline" href={contractHref}>
            계약서 보기
          </Link>
        </div>
      </header>

      <div className="mx-auto flex w-full max-w-md flex-col gap-y-4 px-4 pt-4">
        <StatusCard place={place} view={view} />

        {waiting.length > 0 && (
          <section
            aria-label="확인할 추가공사"
            className={cn(sectionCardClass(), "flex flex-col gap-y-3 border-2 border-primary")}
            data-testid="customer-project-pending-change">
            <div className="flex flex-col gap-y-1">
              <h2 className="text-base font-semibold">확인해 주실 추가공사가 {waiting.length}건 있어요</h2>
              <ul className="flex flex-col gap-y-0.5 text-sm">
                {waiting.map((co) => (
                  <li key={co.seq} className="flex flex-wrap items-center gap-x-2">
                    <span className="font-medium">{co.title}</span>
                    <span className="text-muted-foreground">변경 {co.seq}차</span>
                    <span aria-hidden>·</span>
                    <Money signed value={co.delta_total} />
                  </li>
                ))}
              </ul>
            </div>
            <Button asChild className="w-full" size="cta" variant="primary">
              <Link data-testid="customer-project-pending-change-link" href={`${contractHref}#change-orders`}>
                내용 보고 확인하기
              </Link>
            </Button>
          </section>
        )}

        {latest && <LatestLogCard comments={logCommentCounts[latest.log.id] ?? 0} latest={latest} token={token} />}

        <QuickMenu contractHref={contractHref} hasThread={thread !== null} token={token} />

        {view.assignee_name && (
          <SectionCard className="flex items-center gap-x-3" data-testid="customer-project-assignee">
            <span
              aria-hidden
              className="flex size-11 shrink-0 items-center justify-center rounded-full bg-accent text-lg font-semibold text-accent-foreground">
              {Array.from(view.assignee_name)[0]}
            </span>
            <div className="min-w-0 flex-1">
              <p className="text-xs text-muted-foreground">담당</p>
              <p className="truncate text-base font-semibold">{view.assignee_name}</p>
            </div>
            {thread && (
              <Button asChild size="sm" variant="outline">
                <a href="#messages">대화하기</a>
              </Button>
            )}
          </SectionCard>
        )}

        <AppPromo />

        <section className="flex scroll-mt-16 flex-col gap-y-2" data-testid="customer-project-phases" id="phases">
          <h2 className="text-base font-semibold">공정</h2>
          {view.phases.length === 0 ? (
            <p className="text-sm text-muted-foreground">아직 공정표가 없습니다.</p>
          ) : (
            <ol className="flex flex-col gap-y-1.5">
              {view.phases.map((p, i) => (
                <SectionCard
                  key={`${i}-${p.name}`}
                  as="li"
                  className="flex items-center justify-between gap-2 rounded-md px-3 py-2 text-sm"
                  padding="none">
                  <div className="min-w-0">
                    <p className="truncate font-medium">{p.name}</p>
                    <p className="text-xs tabular-nums text-muted-foreground">{period(p.start_date, p.end_date)}</p>
                  </div>
                  <Pill className="shrink-0" tone={PHASE_TONE[p.status]}>
                    {PROJECT_PHASE_STATUS_LABEL[p.status]}
                  </Pill>
                </SectionCard>
              ))}
            </ol>
          )}
        </section>

        {changeOrders.length > 0 && (
          <section className="flex flex-col gap-y-2" data-testid="customer-project-change-orders">
            <h2 className="text-base font-semibold">변경공사</h2>
            <ul className="flex flex-col gap-y-1.5">
              {changeOrders.map((co) => (
                <SectionCard
                  key={co.seq}
                  as="li"
                  className="flex flex-wrap items-center gap-2 rounded-md px-3 py-2 text-sm"
                  padding="none">
                  <ChangeOrderStatusBadge status={co.status} />
                  <span className="sr-only">, </span>
                  <span className="font-medium">{co.title}</span>
                  <span className="text-muted-foreground">변경 {co.seq}차</span>
                  <span className="sr-only">, 증감 </span>
                  <Money className="text-muted-foreground" signed value={co.delta_total} />
                  {co.status === "SENT" && (
                    <Link className="ml-auto text-sm font-medium text-primary underline-offset-2 hover:underline" href={`${contractHref}#change-orders`}>
                      확인하기
                    </Link>
                  )}
                </SectionCard>
              ))}
            </ul>
          </section>
        )}

        {thread && (
          <section className="flex scroll-mt-16 flex-col gap-y-2" data-testid="customer-project-messages" id="messages">
            <h2 className="flex items-center gap-2 text-base font-semibold">
              담당자와 대화
              {unread > 0 && (
                <span data-testid="customer-message-unread">
                  <Pill tone="primary">새 글 {unread}</Pill>
                </span>
              )}
            </h2>
            <MessageList
              change={thread.closed ? undefined : { target: { kind: "customer", token }, who: { kind: "CUSTOMER" } }}
              emptyText="궁금한 점을 남겨 주시면 담당자가 답해 드립니다."
              messages={thread.messages}
              photoPathOf={(m) => `/api${customerMessagePhotoPath(token, m.id)}`}
              testId="customer-message-list"
              viewer="CUSTOMER"
            />
            {seenUntil && <MessageSeenMarker seenUntil={seenUntil} token={token} />}
            {thread.closed ? (
              <p className="text-sm text-muted-foreground" data-testid="customer-message-closed">
                {MESSAGE_CLOSED_MESSAGE}
              </p>
            ) : (
              <MessageCompose
                placeholder="담당자에게 남길 말을 적어 주세요"
                target={{ kind: "customer", token }}
                testId="customer-message-form"
              />
            )}
          </section>
        )}

        {canReview(view.status) && (
          <section className="flex flex-col gap-y-2" data-testid="customer-project-review" id="review">
            <h2 className="text-base font-semibold">{reviewShare?.review ? "남기신 후기" : "후기 남기기"}</h2>
            <p className="text-sm text-muted-foreground">
              {reviewShare?.review
                ? "언제든 글을 고치거나 공개 동의를 바꿀 수 있습니다."
                : "공사가 끝났습니다. 남겨 주신 글은 담당 시공사에 전해집니다."}
            </p>
            <SectionCard padding="sm">
              <ReviewForm
                namedLabel={reviewShare?.named_label ?? reviewAuthorLabelOf(null, false)}
                review={
                  reviewShare?.review
                    ? {
                        body: reviewShare.review.body,
                        author_label: reviewShare.review.author_label,
                        consent_public: reviewShare.review.consent_public,
                      }
                    : null
                }
                token={token}
              />
            </SectionCard>
          </section>
        )}

        <section className="flex scroll-mt-16 flex-col gap-y-2" data-testid="customer-project-days" id="days">
          <h2 className="text-base font-semibold">현장 소식</h2>
          {view.days.length === 0 ? (
            <p className="text-sm text-muted-foreground">아직 올라온 소식이 없습니다.</p>
          ) : (
            <ol className="flex flex-col gap-y-3">
              {view.days.map((day) => (
                <SectionCard key={day.date} as="li" className="flex flex-col gap-y-2 text-sm" padding="sm">
                  <p className="font-semibold tabular-nums">{formatDate(day.date)}</p>
                  {day.logs.map((log) => (
                    <div key={log.id} className="flex flex-col gap-y-1">
                      <div className="flex flex-wrap items-center gap-2">
                        <Pill tone={LOG_KIND_TONE[log.kind]}>{PROJECT_LOG_KIND_LABEL[log.kind]}</Pill>
                        {log.phase_name && (
                          <span className="text-xs text-muted-foreground" data-testid="customer-log-phase">
                            {log.phase_name}
                          </span>
                        )}
                      </div>
                      <p className="whitespace-pre-wrap break-words">{log.body}</p>
                      {log.weather && <p className="text-xs text-muted-foreground">날씨 {log.weather}</p>}
                      <Link
                        className="tap-target relative self-start text-sm font-medium text-primary underline-offset-2 hover:underline"
                        data-testid="customer-log-link"
                        href={customerLogPath(token, log.id)}>
                        {logCommentCounts[log.id] ? `댓글 ${logCommentCounts[log.id]}` : "댓글 남기기"}
                      </Link>
                    </div>
                  ))}
                  {day.photos.length > 0 && <PhotoGrid photos={day.photos} testId="customer-day-photos" token={token} />}
                </SectionCard>
              ))}
            </ol>
          )}
        </section>

        {view.photos.length > 0 && (
          <section className="flex flex-col gap-y-2" data-testid="customer-project-gallery">
            <h2 className="text-base font-semibold">사진 모아보기</h2>
            <PhotoGrid photos={view.photos} testId="customer-gallery-photos" token={token} />
          </section>
        )}
      </div>
      <AppStartFootLink />
      <CustomerFooter className="px-4" show={data.footer} />
    </CustomerScreenShell>
  );
};

export default Page;
